/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  useAppFeatureFlags,
  useAppSettings,
  useAudioDeviceDisconectedHandler,
  useDialog,
  useEMDR,
  useIsRecording,
  useIsGuestCall,
  useParticipants,
  useRoomHost,
  useScreenShareParticipant,
  useScreenSize,
  useWhiteboard,
} from 'hooks';
import { IEMDRContext, IWhiteboardContext } from 'context';
import { Chat, SimpleInfoDialog } from 'components';
import { WaitingQueue } from '../WaitingQueue';
import { MainControls } from '../MainControls';
import { SidebarControls } from '../SidebarControls';
import { RightSidebar } from '../RightSidebar';
import { ParticipantAudioTracks } from '../ParticipantAudioTracks';
import { SpeakerView } from '../SpeakerView';
import { GridView } from '../GridView';
import { BottomBar } from '../BottomBar';
import { useAppSelector } from 'store';
import { selectCall } from 'store/call';
import { SELECTED_LAYOUT_TYPE_KEY } from '../../../../constants';
import { isHipaaLink } from '../../../../config/productProfile';
import { getHipaaLogoSrc, getVia3LogoSrc } from '../../../../theme';

import styles from './Room.module.scss';

export function Room(): JSX.Element {
  const { t } = useTranslation('providerRoom');
  const { shouldShowMicSpeakerWarning } = useAppFeatureFlags();
  const isGuestCall = useIsGuestCall();
  const forceLightThemeForGuest = isGuestCall && localStorage.getItem('app-theme') === null;
  const theme = useTheme();
  const resolvedThemeMode = theme.customMode || theme.palette.mode;
  const hipaaLogoSrc = forceLightThemeForGuest ? getHipaaLogoSrc('light') : getHipaaLogoSrc(resolvedThemeMode);
  const via3LogoSrc = forceLightThemeForGuest ? getVia3LogoSrc('light') : getVia3LogoSrc(resolvedThemeMode);
  const { isHost } = useRoomHost();
  const { showDialog } = useDialog();
  const screenShareParticipant = useScreenShareParticipant();
  const { isGalleryViewActive } = useAppSettings();
  const { isMobile, isDesktop } = useScreenSize();
  const participants = useParticipants();
  const { state: whiteboardState }: IWhiteboardContext = useWhiteboard();
  const { state: emdrState }: IEMDRContext = useEMDR();
  const isRecording = useIsRecording();

  const call = useAppSelector(selectCall);
  const isGroupCall = call?.isGroup === true;

  useAudioDeviceDisconectedHandler();

  // Tracks auto-switched layout for P2P calls without persisting to localStorage.
  // localStorage is only written when the user explicitly changes layout via the selector.
  const [autoGridActive, setAutoGridActive] = useState(false);
  const shouldForceSpeakerLayout = Boolean(screenShareParticipant) || whiteboardState !== 'off';

  // User override: layoutType comes from localStorage if the user manually selected a layout.
  // When present it always wins; when absent we fall back to the auto-switch state.
  const hasUserPreference = localStorage.getItem(SELECTED_LAYOUT_TYPE_KEY) !== null;
  const effectiveGridActive = hasUserPreference ? isGalleryViewActive : autoGridActive;
  const isEffectiveGalleryViewActive = effectiveGridActive && !shouldForceSpeakerLayout;

  useEffect(() => {
    if (shouldShowMicSpeakerWarning) {
      const modal = showDialog(SimpleInfoDialog, {
        text: t('soundIssuesSwitchDevices'),
        onConfirm: () => {
          modal.destroy();
        },
      });
    }
  }, [shouldShowMicSpeakerWarning, showDialog, t]);

  useEffect(() => {
    if (isGroupCall) return;
    // Respect any layout the user has explicitly stored — don't auto-switch over it.
    if (localStorage.getItem(SELECTED_LAYOUT_TYPE_KEY) !== null) return;
    // P2P with 3 participants → Grid; drops back to 2 → Speaker.
    setAutoGridActive(participants.length >= 2);
  }, [participants.length, isGroupCall]);

  return (
    <Box className={classnames(styles.room, { [styles.guestLight]: forceLightThemeForGuest })}>
      <ParticipantAudioTracks />

      <Stack direction='row' alignItems='center' justifyContent='flex-end' gap='18px' className={styles.logo}>
        {isRecording && isHost && (
          <Box className={styles.transcribingChip}>
            <Typography variant='body5' className={styles.transcribingChipText}>
              {t('transcribing')}
            </Typography>
          </Box>
        )}
        <img
          src={isHipaaLink ? hipaaLogoSrc : via3LogoSrc}
          alt={isHipaaLink ? 'HIPAA LINK' : 'VIA3'}
          className={styles.roomLogo}
        />
      </Stack>

      <Box className={styles.mainContent}>
        {isEffectiveGalleryViewActive ? <GridView /> : <SpeakerView />}
        {isDesktop && <RightSidebar />}
      </Box>
      <Box className={classnames(styles.controls, { [styles.controlsNotHost]: !isHost })}>
        {isHost && (
          <Box
            className={classnames(styles.waitingQueue, {
              [styles.waitingQueueGalery]: !isEffectiveGalleryViewActive,
              [styles.waitingQueueEMDR]: emdrState !== 'off',
            })}
            zIndex={11}
          >
            <WaitingQueue />
          </Box>
        )}

        {isDesktop && <div></div>}

        <Box>
          <MainControls />
        </Box>

        {!isMobile && (
          <Box className={styles.sidebarControls}>
            <SidebarControls />
          </Box>
        )}
      </Box>
      {!isDesktop && <BottomBar />}
      <Chat shouldIgnoreReceiveMessages={false} className={styles.chat} forceLightTheme={forceLightThemeForGuest} />
    </Box>
  );
}
